import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CommonLayout } from 'components/layouts';
import { Notify } from 'components/core';
import { useRecoilState, useSetRecoilState } from 'recoil';
import {
  rolesState,
  currentRoleState,
  editItemState,
  newItemState,
  usersForCurrentRoleState,
  Actions,
  PermissionsList,
} from 'states';
import { ConfigLayoutRoles } from '../../layouts/config-view/config-detail/roles/ConfigLayoutRoles';
import { useRolesService, useUserService, useActionsService } from 'services';
import styles from './RolesHome.module.css';

// ── Role categorisation — uses code field from backend (dynamic, not hardcoded names) ──
// Admin scope: roles with SUPER_ADMIN, ROLE_ADMIN, or LEVEL_* codes
// Dashboard scope: roles with STATE_ADMIN, DISTRICT_ADMIN, BLOCK_ADMIN, FACILITY_ADMIN codes
// Custom/unknown: anything else
// Role categorisation — uses backend flags (admin, dashboardAdmin, superAdmin)
// Admin scope:     admin: true (Tarento Admin, Super Admin)
// Dashboard scope: dashboardAdmin: true AND admin: false (State/District/Block Admin)
const isAdminRole = (role: any) => role?.admin === true || role?.superAdmin === true;
const isDashboardRole = (role: any) => role?.dashboardAdmin === true && role?.admin !== true;

export const RolesHome = () => {
  const { t } = useTranslation();
  const [roles, setRoles] = useRecoilState(rolesState);
  const [currentRole, setCurrentRole] = useRecoilState(currentRoleState);
  const [, setUsersForCurrentRole] = useRecoilState(usersForCurrentRoleState);
  const [, setActions] = useRecoilState(Actions);
  const [, setPermissionsList] = useRecoilState(PermissionsList);
  const setEditCurrentRole = useSetRecoilState(editItemState);
  const setIsNewItem = useSetRecoilState(newItemState);
  const roleService = useRolesService();
  const userService = useUserService();
  const actionService = useActionsService();
  const isSelected = Boolean(currentRole?.id || currentRole?.name === 'New Role');

  const [userCounts, setUserCounts] = useState<Record<number, number>>({});
  const [searchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'admin' | 'dashboard'>('all');
  const [adminInfoOpen, setAdminInfoOpen] = useState(false);
  const [dashInfoOpen, setDashInfoOpen] = useState(false);

  // Reset to overview on every mount — prevents stale role detail showing on nav click
  useEffect(() => {
    setCurrentRole({} as any);
    setEditCurrentRole(false);
  }, []);

  useEffect(() => {
    roleService.getRoles().then(
      (response: any) => {
        if (response.length > 0) setRoles(response);
      },
      (error: any) => Notify.error(error)
    );

    userService
      .getUsers(true)
      .then((users: any[]) => {
        const counts: Record<number, number> = {};
        users.forEach((u: any) => {
          const roleId = u.roles?.[0]?.id;
          if (roleId) counts[roleId] = (counts[roleId] || 0) + 1;
        });
        setUserCounts(counts);
      })
      .catch(() => {
        // user counts are best-effort — ignore failures
      });
  }, []);

  // ── Select a role — mirrors selectRole() from ListViewLayout exactly ──────
  const handleRoleClick = (role: any) => {
    if (!role.id || role.id === currentRole.id) return;
    setEditCurrentRole(false);
    // Remove any unsaved new role
    const unsavedIdx = roles.findIndex((r: any) => r.id === undefined);
    if (unsavedIdx !== -1) {
      const updated = [...roles];
      updated.splice(unsavedIdx, 1);
      setRoles(updated);
      setIsNewItem(false);
    }
    setCurrentRole(role);
    // Load users, all actions, and role-specific permissions — same as original
    userService.getUsersForRole(role.id).then((u: any) => setUsersForCurrentRole(u));
    actionService.getAllActions().then((r: any) => {
      if (r?.length) setActions(r);
    });
    actionService.getActionsForRole(role.id).then((r: any) => {
      if (r?.length) setPermissionsList(r);
    });
  };

  // const handleCreateRole = () => {
  //   if (roles.findIndex((r: any) => r.id === undefined) !== -1) return; // already has unsaved
  //   const newRole = { name: "New Role", description: "role description" };
  //   setRoles((prev: any) => [...prev, newRole]);
  //   setCurrentRole(newRole as any);
  //   setUsersForCurrentRole([]);
  //   setIsNewItem(true);
  //   setEditCurrentRole(false);
  // };

  const handleBack = useCallback(() => {
    const unsavedIdx = roles.findIndex((r: any) => r.id === undefined);
    if (unsavedIdx !== -1) {
      const updated = [...roles];
      updated.splice(unsavedIdx, 1);
      setRoles(updated);
      setIsNewItem(false);
    }
    setCurrentRole({} as any);
    setEditCurrentRole(false);
  }, [roles, setRoles, setIsNewItem, setCurrentRole, setEditCurrentRole]);

  const filteredRoles = roles.filter((r: any) => {
    const matchSearch = (r.name || '').toLowerCase().includes(searchQuery.toLowerCase());
    let matchType = isDashboardRole(r);
    if (typeFilter === 'all') {
      matchType = true;
    } else if (typeFilter === 'admin') {
      matchType = isAdminRole(r);
    }
    return matchSearch && matchType;
  });

  const adminRoles = roles.filter((r: any) => isAdminRole(r));
  const dashboardRoles = roles.filter((r: any) => isDashboardRole(r));
  const adminUserCount = adminRoles.reduce(
    (sum: number, r: any) => sum + (userCounts[r.id] || 0),
    0
  );
  const dashUserCount = dashboardRoles.reduce(
    (sum: number, r: any) => sum + (userCounts[r.id] || 0),
    0
  );

  // ── Show ConfigLayoutRoles when a role is selected ────────────────────────
  if (isSelected) {
    return (
      <CommonLayout>
        <div className={styles.pageArea}>
          <div className={styles.fullWidth}>
            <ConfigLayoutRoles onBack={handleBack} />
          </div>
        </div>
      </CommonLayout>
    );
  }

  return (
    <CommonLayout>
      <div className={styles.pageArea}>
        <div className={styles.overviewPage}>
          {/* Toolbar */}
          {/* TODO: Uncomment search and create role button once role creation form fields are complete
          <div className={styles.toolbar}>
            <div className={styles.searchWrapper}>
              <span className="material-icons" style={{ fontSize: 18, color: "var(--text-secondary)" }}>search</span>
              <input
                className={styles.searchInput}
                placeholder="Search roles..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <span
                  className="material-icons"
                  style={{ fontSize: 16, color: "var(--text-secondary)", cursor: "pointer" }}
                  onClick={() => setSearchQuery("")}
                >close</span>
              )}
            </div>
            <button className={styles.createBtn} onClick={handleCreateRole}>
              <span className="material-icons">add</span>
              Create Role
            </button>
          </div>
          */}

          {/* Widget cards — just counts, no icons */}
          <div className={styles.widgetRow}>
            {/* Admin scope */}
            <div className={styles.widget}>
              <div className={styles.widgetLeft}>
                <div>
                  <div className={styles.widgetCount}>{adminRoles.length}</div>
                  <div className={styles.widgetLabel}>{t('ROLE.ADMIN_SCOPE_ROLES')}</div>
                </div>
              </div>
              <div className={styles.widgetRight}>
                <div className={styles.widgetStat}>
                  <div className={styles.widgetStatNum}>{adminUserCount}</div>
                  <div className={styles.widgetStatLabel}>{t('ROLE.TOTAL_USERS')}</div>
                </div>
                <div className={styles.infoBtnWrap}>
                  <button
                    className={styles.infoBtn}
                    onClick={() => {
                      setAdminInfoOpen((v) => !v);
                      setDashInfoOpen(false);
                    }}
                  >
                    <span className="material-icons">info_outline</span>
                  </button>
                  {adminInfoOpen && (
                    <>
                      <div
                        className={styles.infoBackdrop}
                        onClick={() => setAdminInfoOpen(false)}
                      />
                      <div className={styles.infoPopup}>
                        <p className={styles.infoPopupText}>
                          {t('ROLE.ADMIN_SCOPE_INFO')}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Dashboard scope */}
            <div className={`${styles.widget} ${styles.widgetDash}`}>
              <div className={styles.widgetLeft}>
                <div>
                  <div className={`${styles.widgetCount} ${styles.widgetCountDash}`}>
                    {dashboardRoles.length}
                  </div>
                  <div className={styles.widgetLabel}>{t('ROLE.DASHBOARD_SCOPE_ROLES')}</div>
                </div>
              </div>
              <div className={styles.widgetRight}>
                <div className={styles.widgetStat}>
                  <div className={`${styles.widgetStatNum} ${styles.widgetStatNumDash}`}>
                    {dashUserCount}
                  </div>
                  <div className={styles.widgetStatLabel}>{t('ROLE.TOTAL_USERS')}</div>
                </div>
                <div className={styles.infoBtnWrap}>
                  <button
                    className={styles.infoBtn}
                    onClick={() => {
                      setDashInfoOpen((v) => !v);
                      setAdminInfoOpen(false);
                    }}
                  >
                    <span className="material-icons">info_outline</span>
                  </button>
                  {dashInfoOpen && (
                    <>
                      <div className={styles.infoBackdrop} onClick={() => setDashInfoOpen(false)} />
                      <div className={styles.infoPopup}>
                        <p className={styles.infoPopupText}>
                          {t('ROLE.DASHBOARD_SCOPE_INFO')}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Table toolbar */}
          <div className={styles.tableToolbar}>
            <span className={styles.tableTitle}>{t('ROLE.ALL_ROLES')}</span>
            <div className={styles.tableToolbarRight}>
              <span className={styles.tableCount}>{t('ROLE.ROLES_COUNT', { count: filteredRoles.length })}</span>
              <select
                className={styles.typeSelect}
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as 'all' | 'admin' | 'dashboard')}
              >
                <option value="all">{t('ROLE.FILTER_ALL_TYPES')}</option>
                <option value="admin">{t('ROLE.FILTER_ADMIN_SCOPE')}</option>
                <option value="dashboard">{t('ROLE.FILTER_DASHBOARD_SCOPE')}</option>
              </select>
            </div>
          </div>

          {/* Roles table */}
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.th}>{t('ROLE.TABLE_ROLE_NAME')}</th>
                  <th className={styles.th}>{t('ROLE.TABLE_TYPE')}</th>
                  <th className={styles.th}>{t('ROLE.TABLE_DESCRIPTION')}</th>
                  <th className={styles.th} style={{ textAlign: 'center' }}>
                    {t('ROLE.TABLE_USERS')}
                  </th>
                  <th className={styles.th}></th>
                </tr>
              </thead>
              <tbody>
                {filteredRoles.length === 0 && (
                  <tr>
                    <td colSpan={5} className={styles.noData}>
                      {t('ROLE.NO_ROLES')}
                    </td>
                  </tr>
                )}
                {filteredRoles.map((role: any) => {
                  const count = userCounts[role.id] || 0;
                  const isAdmin = isAdminRole(role);
                  const isDash = isDashboardRole(role);
                  let typeLabel = t('ROLE.BADGE_CUSTOM');
                  let typeBadgeClass = styles.typeCustom;
                  if (isAdmin) {
                    typeLabel = t('ROLE.BADGE_ADMIN');
                    typeBadgeClass = styles.typeAdmin;
                  } else if (isDash) {
                    typeLabel = t('ROLE.BADGE_DASHBOARD');
                    typeBadgeClass = styles.typeDash;
                  }
                  return (
                    <tr key={role.id} className={styles.tr} onClick={() => handleRoleClick(role)}>
                      <td className={styles.td}>
                        <span className={styles.roleName}>{role.name}</span>
                      </td>
                      <td className={styles.td}>
                        <span
                          className={`${styles.typeBadge} ${typeBadgeClass}`}
                        >
                          {typeLabel}
                        </span>
                      </td>
                      <td className={styles.td}>
                        <span className={styles.roleDesc}>{role.description || '—'}</span>
                      </td>
                      <td className={styles.td} style={{ textAlign: 'center' }}>
                        <span className={styles.userCountBadge}>{count}</span>
                      </td>
                      <td className={styles.td} style={{ textAlign: 'right' }}>
                        <span
                          className="material-icons"
                          style={{ fontSize: 16, color: 'var(--text-secondary)' }}
                        >
                          arrow_forward
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </CommonLayout>
  );
};
